import { Avatar, Loader, Modal, Text } from "@mantine/core";
import { useClipboard, useDisclosure } from "@mantine/hooks";
import { Slot } from "@radix-ui/react-slot";
import IconBrandWhatsapp from "@src/assets/icons/tabler/brand-whatsapp.svg?react";
import IconCheck from "@src/assets/icons/tabler/check.svg?react";
import IconCopy from "@src/assets/icons/tabler/copy.svg?react";
import IconExternalLink from "@src/assets/icons/tabler/link.svg?react";
import {
	ErrorContent,
	getUnknownErrorMessage,
	getUnknownErrorStack,
} from "@src/components/errors/boundary";
import { AppQrCode } from "@src/components/qr-code/app-qr-code";
import { openExternalLink } from "@src/components/qr-code/qr-code-utils";
import type { WhatsappModalOpenSource } from "@src/utils/analytics";
import { captureWhatsappModalOpened } from "@src/utils/analytics";
import { showSuccessNotification } from "@src/utils/notifications";
import { type ReactNode, Suspense, useEffect, useRef, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useGenerateActivationLink } from "./hooks/use-generate-activation-link";
import { useWhatsAppConnectedSubscription } from "./hooks/use-whatsapp-connected-subscription";

function WhatsAppIntegrationModalContent(props: { onClose: () => void }) {
	const { activationLink } = useGenerateActivationLink();
	const clipboard = useClipboard({ timeout: 2000 });
	const [connectedPhoneNumber, setConnectedPhoneNumber] = useState<
		string | null
	>(null);
	const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		return () => {
			if (closeTimerRef.current) {
				clearTimeout(closeTimerRef.current);
			}
		};
	}, []);

	useWhatsAppConnectedSubscription({
		onConnected: (payload) => {
			setConnectedPhoneNumber(payload.phoneNumber);
			showSuccessNotification(
				"WhatsApp Connected",
				`Your WhatsApp number +${payload.phoneNumber.replace(/^\+/, "")} is connected to Draft.`,
			);
			closeTimerRef.current = setTimeout(() => {
				props.onClose();
			}, 1200);
		},
	});

	if (connectedPhoneNumber) {
		return (
			<div
				aria-live="polite"
				className="flex flex-col items-center justify-center py-10 px-4 text-center space-y-4"
			>
				<div className="relative">
					<div className="absolute inset-0 bg-whatsapp-primary blur-xl opacity-30 rounded-full animate-pulse" />
					<div className="relative w-16 h-16 rounded-full bg-whatsapp-primary/10 border-2 border-whatsapp-primary flex items-center justify-center text-whatsapp-primary">
						<IconCheck width={32} height={32} />
					</div>
				</div>
				<div className="space-y-1">
					<Text size="xl" fw={700} className="text-text-primary">
						WhatsApp Connected!
					</Text>
					<Text size="sm" c="dimmed">
						Your number{" "}
						<span className="font-mono font-semibold text-text-primary">
							+{connectedPhoneNumber.replace(/^\+/, "")}
						</span>{" "}
						has been verified and connected to Draft.
					</Text>
				</div>
				<Text size="xs" c="dimmed">
					Closing window...
				</Text>
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-5 sm:gap-6">
			{/* Header Section */}
			<div className="flex items-center gap-4">
				<div className="relative shrink-0">
					<div className="absolute inset-0 bg-whatsapp-primary blur-xl opacity-20 rounded-full animate-pulse" />
					<Avatar
						variant="gradient"
						gradient={{
							from: "var(--ui-color-whatsapp-primary)",
							to: "var(--ui-color-whatsapp-secondary)",
							deg: 135,
						}}
						radius={9999}
						className="shadow-md border-2 border-surface-default w-12 h-12 sm:w-16 sm:h-16"
					>
						<IconBrandWhatsapp
							className="w-6 h-6 sm:w-8 sm:h-8 text-text-inverse"
							color="white"
						/>
					</Avatar>
				</div>
				<div>
					<Text
						size="lg"
						fw={700}
						className="tracking-tight text-text-primary sm:text-xl"
					>
						Connect WhatsApp to Draft
					</Text>
					<Text
						size="xs"
						c="dimmed"
						className="max-w-60 leading-relaxed sm:text-sm sm:max-w-95"
					>
						Sync your thoughts instantly. Send notes from WhatsApp to your
						Draft.
					</Text>
				</div>
			</div>

			<div className="h-px bg-border-subtle w-full" />

			{/* Setup Steps */}
			<div className="flex flex-col gap-4">
				<div>
					<p className="mb-1 uppercase tracking-wider text-text-muted text-xs font-bold">
						Step 1: Connect
					</p>
					<p className="sm:text-sm text-text-secondary text-xs">
						Scan the QR code below or use the direct link to start the
						activation chat.
					</p>
				</div>

				{activationLink ? (
					<div className="flex flex-col items-center gap-4">
						{/* QR Code Card */}
						<div className="relative group p-4 bg-surface-default rounded-4xl border border-border-default shadow-sm transition-all duration-300 hover:shadow-md sm:p-6">
							<div className="absolute -top-1 -right-1 bg-whatsapp-primary text-text-inverse p-1 rounded-full shadow-sm sm:-top-2 sm:-right-2 sm:p-1.5">
								<IconBrandWhatsapp width={14} height={14} />
							</div>
							<div className="p-2 bg-white rounded-2xl border border-border-subtle shadow-inner sm:p-3">
								<AppQrCode
									value={activationLink}
									size={undefined}
									className="block mx-auto w-full h-full max-w-40 max-h-40 sm:max-w-45 sm:max-h-45 text-text-primary"
								/>
							</div>

							<div className="mt-3 flex flex-col items-center gap-1">
								<div className="w-1.5 h-1.5 rounded-full bg-whatsapp-primary animate-ping" />
								<Text size="xs" fw={500} c="dimmed">
									Waiting for scan...
								</Text>
							</div>
						</div>

						{/* Quick Actions */}
						<div className="w-full space-y-1.5">
							<p className="px-1 text-xs font-bold text-text-muted">
								OR USE LINK
							</p>
							<div className="flex items-center gap-2 p-1 pl-3 bg-surface-subtle border border-border-default rounded-xl">
								<p className="flex-1 truncate text-text-secondary font-mono opacity-70 text-xs">
									{activationLink}
								</p>
								<div className="flex items-center gap-2">
									<button
										type="button"
										onClick={() => openExternalLink(activationLink)}
										className="p-1.5 hover:bg-surface-hover-subtle rounded-lg transition-colors sm:p-2"
										title="Open in WhatsApp"
									>
										<IconExternalLink
											width={16}
											height={16}
											className="text-text-secondary"
										/>
									</button>
									<button
										type="button"
										onClick={() => clipboard.copy(activationLink)}
										className="p-1.5 hover:bg-surface-hover-subtle rounded-lg transition-colors sm:p-2"
										title="Copy link"
									>
										{clipboard.copied ? (
											<IconCheck
												width={16}
												height={16}
												className="text-green-500"
											/>
										) : (
											<IconCopy
												width={16}
												height={16}
												className="text-text-secondary"
											/>
										)}
									</button>
								</div>
							</div>
						</div>
					</div>
				) : (
					<div className="h-50 flex items-center justify-center bg-surface-subtle rounded-3xl border border-dashed border-border-default sm:h-70">
						<WhatsAppLoader />
					</div>
				)}

				<div>
					<p className="mb-1 uppercase tracking-wider text-text-muted text-xs font-bold">
						Step 2: Start Chat
					</p>
					<p className="sm:text-sm text-text-secondary text-xs">
						Once the chat opens, send the pre-filled activation message.
					</p>
				</div>
			</div>
		</div>
	);
}

export function WhatsAppIntegrationModalButton(props: {
	children: ReactNode;
	source: WhatsappModalOpenSource;
}) {
	const [opened, { open, close }] = useDisclosure(false);
	const handleOpen = () => {
		captureWhatsappModalOpened(props.source);
		open();
	};

	return (
		<>
			<Modal
				opened={opened}
				onClose={close}
				centered
				withCloseButton={false}
				zIndex={2000}
				size="600px"
				padding={0}
				classNames={{
					content:
						"relative overflow-hidden shadow-2xl border border-border-subtle bg-surface-default mx-4 sm:mx-0 rounded-2xl",
				}}
			>
				<div className="max-h-[92vh] p-6 sm:p-8 overflow-y-auto">
					<ErrorBoundary
						fallbackRender={({ error }) => {
							return (
								<div className="flex flex-col gap-4 p-4">
									<Text size="lg" fw={700}>
										Something's gone wrong.
									</Text>
									<ErrorContent
										errorMessage={getUnknownErrorMessage(error)}
										errorStack={getUnknownErrorStack(error)}
									/>
								</div>
							);
						}}
					>
						<Suspense fallback={<WhatsAppLoader />}>
							<WhatsAppIntegrationModalContent onClose={close} />
						</Suspense>
					</ErrorBoundary>
				</div>
			</Modal>

			<Slot onClick={handleOpen} className="cursor-pointer">
				{props.children}
			</Slot>
		</>
	);
}

function WhatsAppLoader() {
	return (
		<div className="flex flex-col items-center gap-2">
			<Loader
				size="md"
				color="var(--ui-color-whatsapp-primary)"
				variant="dots"
			/>
			<Text size="xs" fw={500} c="dimmed">
				Generating secure link...
			</Text>
		</div>
	);
}
